import { useEffect, useState, type FormEvent } from 'react'
import { PASSWORD_MIN } from '@sp/shared'
import { useSignedIn } from '../session.tsx'
import { api } from '../lib/api.ts'
import { BACKGROUNDS, applyBackground, currentBackground, type BackgroundId } from '../lib/background.ts'
import { errorMessage } from '../lib/useResource.ts'
import { Sheet } from './Sheet.tsx'
import { Button, Divider, Label, Paragraph, TextCol, TextField } from './ui.tsx'
import { PasswordField } from './bits.tsx'
import { confirmLogout } from './GroupSwitcherSheet.tsx'
import { useConfirm } from './ConfirmSheet.tsx'
import { PushSettings } from './PushSettings.tsx'
import { applyLargeText, largeText } from '../lib/textSize.ts'

export function AccountSheet({ open, onClose }: { open: boolean; onClose(): void }) {
  const { confirm, confirmSheet } = useConfirm()
  const s = useSignedIn()
  const [name, setName] = useState(s.user.name)
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [nameMsg, setNameMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const [passMsg, setPassMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [background, setBackground] = useState<BackgroundId>(currentBackground)
  const [bigText, setBigText] = useState(largeText)

  useEffect(() => {
    if (!open) return
    setName(s.user.name)
    setCurrent('')
    setNext('')
    setNameMsg(null)
    setPassMsg(null)
    // Formular se plni jen pri otevreni, ne pri kazdem obnoveni uctu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  async function saveName(e: FormEvent) {
    e.preventDefault()
    if (!name.trim()) {
      setNameMsg({ ok: false, text: 'Jméno nesmí být prázdné' })
      return
    }
    setBusy(true)
    try {
      s.setMe(await api.auth.updateProfile(name.trim()))
      setNameMsg({ ok: true, text: 'Jméno je uložené' })
    } catch (err) {
      setNameMsg({ ok: false, text: errorMessage(err) })
    } finally {
      setBusy(false)
    }
  }

  async function savePassword(e: FormEvent) {
    e.preventDefault()
    if (next.length < PASSWORD_MIN) {
      setPassMsg({ ok: false, text: `Nové heslo musí mít aspoň ${PASSWORD_MIN} znaků` })
      return
    }
    setBusy(true)
    try {
      await api.auth.changePassword(current, next)
      setCurrent('')
      setNext('')
      setPassMsg({ ok: true, text: 'Heslo je změněné. Ostatní zařízení se odhlásila.' })
    } catch (err) {
      setPassMsg({ ok: false, text: errorMessage(err) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Sheet open={open} onClose={onClose} title="Můj účet" subtitle={s.user.email}>
        <div className="stack">
          <form className="stack-8" onSubmit={(e) => void saveName(e)}>
            <TextField
              label="Jméno"
              hint="Tak tě uvidí ostatní ve skupině"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={60}
              autoComplete="nickname"
            />
            {nameMsg && <Paragraph tone={nameMsg.ok ? 'fg' : 'error'}>{nameMsg.text}</Paragraph>}
            <Button type="submit" className="self-start" disabled={busy || name.trim() === s.user.name}>
              Uložit jméno
            </Button>
          </form>
          <Divider />
          <form className="stack-8" onSubmit={(e) => void savePassword(e)}>
            <Label>Změna hesla</Label>
            <PasswordField
              label="Současné heslo"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
            />
            <PasswordField
              label="Nové heslo"
              hint={`Aspoň ${PASSWORD_MIN} znaků`}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
            />
            {passMsg && <Paragraph tone={passMsg.ok ? 'fg' : 'error'}>{passMsg.text}</Paragraph>}
            <Button type="submit" className="self-start" disabled={busy || !current || !next}>
              Změnit heslo
            </Button>
          </form>
          <Divider />
          <PushSettings />
          <Divider />
          <div className="stack-8">
            <Label>Pozadí</Label>
            <div className="bg-picks" role="radiogroup" aria-label="Pozadí">
              {BACKGROUNDS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  role="radio"
                  aria-checked={background === option.id}
                  className="bg-pick"
                  onClick={() => {
                    applyBackground(option.id)
                    setBackground(option.id)
                  }}
                >
                  <span className={`bg-pick-swatch is-${option.id}`} aria-hidden="true" />
                  {option.label}
                </button>
              ))}
            </div>
            <Paragraph size="meta">Platí jen v tomhle zařízení.</Paragraph>
          </div>
          <button
            type="button"
            className="tpl-watch"
            role="switch"
            aria-checked={bigText}
            onClick={() => {
              applyLargeText(!bigText)
              setBigText(!bigText)
            }}
          >
            <span className="big-text-icon" aria-hidden="true">
              Aa
            </span>
            <TextCol title="Větší písmo" sub="Celá aplikace o kus větší. Platí jen v tomhle zařízení." />
            <span className={`switch${bigText ? ' is-on' : ''}`} aria-hidden="true" />
          </button>
          <Divider />
          <Button kind="danger" className="self-start" compact onClick={() => void confirmLogout(s.logout, confirm)}>
            Odhlásit se
          </Button>
        </div>
      </Sheet>
      {confirmSheet}
    </>
  )
}
